<template>
  <div class="col-span-2 sm:col-span-2">
    <div class="relative">
      <div class="absolute inset-0 flex items-center" aria-hidden="true">
        <div class="w-full border-t border-gray-300" />
      </div>
      <div class="relative flex justify-start">
        <span class="pr-3 bg-light-grey text-lg font-medium text-vice-teal-700">Past sessions</span>
      </div>
    </div>

    <div class="flex flex-col mt-8" v-if="pastSessionsExist">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div class="shadow-sm overflow-hidden border border-gray-200 rounded-lg">
            <table class="table-fixed min-w-full divide-y divide-gray-50">
              <thead>
                <tr class="border-b border-gray-200">
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Start
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Total duration
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Attendees
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Access
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xxs font-medium text-gray-500 tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(session, sessionIdx) in this.$page.props.sessions.past_sessions.data"
                  :key="session.unique_id"
                  :class="sessionIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <td class="px-6 py-4 text-xs font-medium text-vice-teal-900">
                    <span
                      class="cursor-pointer"
                      @click="Inertia.visit(route('session.clinician.details', session.unique_id))"
                    >
                      {{ session.title }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-left whitespace-nowrap text-xs text-gray-500">
                    {{ yearFormatter(session.details.session_start_by_timezone) }}
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                    {{ hoursFormatter(session.details.session_start_by_timezone) }}
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                    {{ durationFormatter(session.analytics?.total_duration || 0) }}
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                    {{ session.presented_count }}
                  </td>
                  <td class="px-6 py-4 text-center">
                    <GroupOfPeopleIcon class="inline-block" v-if="!session.is_private" />
                    <LockIcon class="inline-block" v-if="session.is_private" />
                  </td>
                  <!-- Session Actions -->
                  <td :data-cy="`duplicate-session-${session.unique_id}`" class="text-center">
                    <ViceLoader
                      v-if="isDuplicating === session.unique_id"
                      class="pl-4 pr-1 relative ml-0.5 mr-0 h-5 w-5"
                      colorClass="text-vice-teal-900"
                    />
                    <jet-dropdown v-else align="right" width="48">
                      <template #trigger>
                        <span
                          class="
                            select-none
                            cursor-pointer
                            px-4
                            text-xl
                            font-medium
                            text-vice-teal-900
                          "
                        >
                          ⋯
                        </span>
                      </template>
                      <template #content>
                        <jet-dropdown-link as="button" @click="duplicateSession(session.unique_id)">
                          Duplicate session
                        </jet-dropdown-link>
                      </template>
                    </jet-dropdown>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div
        class="flex items-center justify-between mt-1 px-2 py-3"
        v-if="$page.props.sessions.past_sessions"
      >
        <div class="flex-1 flex items-center gap-1 justify-between">
          <div class="flex items-center space-x-2">
            <label for="per-page" class="text-sm text-gray-700">Items per page:</label>
            <select
              id="per-page"
              class="
                block
                pl-3
                pr-10
                py-1.5
                sm:py-2
                text-base
                border-gray-300
                focus:outline-none
                focus:ring-vice-teal-600
                focus:border-vice-teal-600
                sm:text-sm
                rounded-md
              "
              v-model.number="perPage"
              @change="changePerPage"
            >
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex justify-end">
            <button
              type="button"
              class="
                relative
                inline-flex
                items-center
                px-4
                py-2
                border border-gray-300
                text-sm
                font-medium
                rounded-md
                text-gray-700
                bg-white
                hover:bg-gray-50
                disabled:opacity-50
                whitespace-nowrap
              "
              :class="{ 'pointer-events-none': !$page.props.sessions.past_sessions.prev_page_url }"
              :disabled="!$page.props.sessions.past_sessions.prev_page_url"
              @click="goTo($page.props.sessions.past_sessions.prev_page_url)"
            >
              ‹ Previous
            </button>
            <button
              type="button"
              class="
                ml-1
                sm:ml-3
                relative
                inline-flex
                items-center
                px-4
                py-2
                border border-gray-300
                text-sm
                font-medium
                rounded-md
                text-gray-700
                bg-white
                hover:bg-gray-50
                disabled:opacity-50
                whitespace-nowrap
              "
              :disabled="!$page.props.sessions.past_sessions.next_page_url"
              @click="goTo($page.props.sessions.past_sessions.next_page_url)"
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 text-gray-500 text-center" v-else>
      Sessions in which you participated will appear here.
    </div>
  </div>
</template>

<script>
import { Inertia } from '@inertiajs/inertia';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import GroupOfPeopleIcon from '@/Components/Icons/GroupOfPeopleIcon.vue';
import LockIcon from '@/Components/Icons/LockIcon.vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import JetDropdown from '@/Jetstream/Dropdown.vue';
import JetDropdownLink from '@/Jetstream/DropdownLink.vue';

export default {
  name: 'PastSessions',
  setup() {
    return {
      userType,
    };
  },
  components: {
    ViceLoader,
    LockIcon,
    GroupOfPeopleIcon,
    JetDropdown,
    JetDropdownLink,
  },
  mixins: [DateFormatMixin],
  data() {
    const defaultPerPage = this.$page?.props?.sessions?.past_sessions?.per_page || 20;
    return {
      perPage: defaultPerPage,
      isDuplicating: '',
    };
  },
  computed: {
    Inertia() {
      return Inertia;
    },
    pastSessionsExist() {
      const ps = this.$page.props.sessions.past_sessions;
      return ps && ps.data && ps.data.length > 0;
    },
  },
  mounted() {
    try {
      let isReload = false;
      const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length) {
        isReload = navEntries[0].type === 'reload';
      } else if (performance && performance.navigation) {
        // Deprecated API fallback
        isReload = performance.navigation.type === 1; // 1 = TYPE_RELOAD
      }

      if (isReload) {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const currentPage = params.get('page');
        // If on a page greater than 1 after a reload, reset to first page
        if (currentPage && currentPage !== '1') {
          params.set('page', '1');
          const newUrl = `${url.pathname}?${params.toString()}`;
          Inertia.replace(newUrl, { preserveScroll: true, preserveState: true });
        }
      }
    } catch (e) {
      // no-op
    }
  },
  methods: {
    goTo(url) {
      if (!url) return;
      Inertia.visit(url, { preserveScroll: true, preserveState: true });
    },
    changePerPage() {
      try {
        const params = new URLSearchParams(window.location.search);
        params.set('per_page', String(this.perPage));
        params.set('page', '1');
        Inertia.visit(`${window.location.pathname}?${params.toString()}`, {
          preserveScroll: true,
          preserveState: true,
        });
      } catch (e) {
        // Fallback: direct navigation
        const params = new URLSearchParams(window.location.search);
        params.set('per_page', String(this.perPage));
        params.set('page', '1');
        window.location.href = `${window.location.pathname}?${params.toString()}`;
      }
    },
    duplicateSession(uniqueId) {
      this.isDuplicating = uniqueId;
      Inertia.post(
        route('duplicate.session', uniqueId),
        {},
        {
          preserveState: false,
          onSuccess: () => {
            // Redirect is handled by backend
          },
          onError: (errors) => {
            console.error('Duplicating error:', errors);
            this.isDuplicating = '';
          },
          onFinish: () => {
            this.isDuplicating = '';
          },
        },
      );
    },
  },
};
</script>
